import React, { useRef } from 'react';
import { Typography, Box, Checkbox, Tooltip, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDraggable } from '@dnd-kit/core';
import { useFileUrl } from '../../../../hooks/useFileUrl';
import { supportsPreview } from '../../../../utils/supportsPreview';
import { getFileIcon } from '../../../../utils/getFileIcon';
import { getFileTypeColor } from '../../../../utils/getFileTypeColor';
import { FileActionsMenu } from '../../modules/FileActionsMenu';
import { InlineRenameField, hasAction, useInlineRename } from '../../modules/InlineRename';
import { AccessMark, SharedFileMark } from '../../modules/FolderGlyph';

export const FileCard = ({
  file,
  selected = false,
  secondarySelected = false,
  disabled = false,
  onClick,
  showCheckbox = false,
  dragData,
  actions,
  onFileAction,
  checkable = false,
  checked = false,
  onToggleCheck,
  // (file, newName) => Promise<boolean>; with a "rename" action, the name can be edited in place
  onRename,
}) => {
  const cardRef = useRef(null);
  const { renaming, stopRenaming, nameProps, rowKeyDown } = useInlineRename({
    enabled: Boolean(onRename) && hasAction(actions, 'rename') && !disabled,
  });
  const isImage = supportsPreview(file.filetype);
  const { url: imageDataUrl, loading } = useFileUrl(isImage ? file.file_id : null, { size: '128x128' });

  const fileIcon = getFileIcon(file.filetype || 'application/octet-stream', 64);
  const fileColor = getFileTypeColor(file.filetype);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable(
    dragData ?? { id: `drag-disabled-${file.file_id}`, disabled: true }
  );

  const handleClick = (e) => {
    // The second click of a double-click (renaming) mustn't select again
    if (disabled || e.detail > 1) return;
    if (onClick) onClick(file, e);
  };

  return (
    <Box
      ref={(el) => { setNodeRef(el); cardRef.current = el; }}
      {...(dragData && !dragData.disabled && !disabled ? { ...listeners, ...attributes } : {})}
      tabIndex={disabled ? -1 : 0}
      aria-selected={selected || secondarySelected}
      onKeyDown={(e) => {
        if (disabled || e.target !== e.currentTarget || rowKeyDown(e)) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
      sx={{
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        '&:hover .file-card-actions, &:focus-within .file-card-actions': { opacity: 1 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: isDragging ? 'grabbing' : (disabled ? 'not-allowed' : 'pointer'),
        transition: 'all 0.2s',
        p: 1,
        borderRadius: 2,
        opacity: isDragging ? 0.4 : (disabled ? 0.5 : 1),
        // A tint plus outline rather than a solid fill, so the name stays readable
        backgroundColor: (theme) => (selected || secondarySelected)
          ? alpha(theme.palette[selected ? 'info' : 'warning'].main, 0.16)
          : 'transparent',
        boxShadow: (theme) => (selected || secondarySelected)
          ? `inset 0 0 0 2px ${theme.palette[selected ? 'info' : 'warning'].main}`
          : 'none',
        '&:hover': {
          backgroundColor: (theme) => (selected || secondarySelected)
            ? alpha(theme.palette[selected ? 'info' : 'warning'].main, 0.16)
            : theme.palette.action.hover,
        },
        position: 'relative',
      }}
      onClick={handleClick}
    >
      {/* Checkbox for multi-select */}
      {(showCheckbox || checkable) && !disabled && (
        <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}>
          <Checkbox
            checked={checkable ? checked : (selected || secondarySelected)}
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { if (checkable) e.stopPropagation(); }}
            onChange={() => { if (checkable) onToggleCheck?.(file); }}
            sx={{ backgroundColor: 'background.paper', '&:hover': { backgroundColor: 'background.paper' } }}
          />
        </Box>
      )}

      {actions?.length > 0 && !disabled && (
        <Box
          className="file-card-actions"
          sx={{ position: 'absolute', top: 2, right: 2, zIndex: 1, opacity: 0, transition: 'opacity 0.15s', bgcolor: 'background.paper', borderRadius: '50%' }}
        >
          <FileActionsMenu file={file} actions={actions} onAction={onFileAction} />
        </Box>
      )}

      {/* Thumbnail or Icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 1,
          mb: 1,
        }}
      >
        {isImage && imageDataUrl ? (
          <Box
            component="img"
            src={imageDataUrl}
            alt={file.filename}
            className="file-image"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 1,
              opacity: 1,
              transition: 'opacity 0.2s',
            }}
          />
        ) : (
          <Box
            className="file-icon"
            sx={{
              color: fileColor,
              opacity: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.2s',
            }}
          >
            {fileIcon}
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            }}
          >
            <CircularProgress size={20} />
          </Box>
        )}

        <SharedFileMark file={file} badge sx={{ position: 'absolute', right: 2, bottom: 2 }} />
        <AccessMark item={file} badge sx={{ position: 'absolute', left: 2, bottom: 2 }} />
      </Box>

      {renaming ? (
        <InlineRenameField
          name={file.filename}
          keepExtension
          onSave={(newName) => onRename(file, newName)}
          onClose={({ keyboard }) => {
            stopRenaming();
            if (keyboard) cardRef.current?.focus();
          }}
          sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem' } }}
        />
      ) : (
        <Tooltip title={file.filename} placement="top">
          <Typography
            variant="caption"
            {...nameProps}
            sx={{
              textAlign: 'center',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.primary',
            }}
          >
            {file.filename}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};
